import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { ClipboardIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";

export default function QuestionnaireSuccessionModal({
  open,
  onClose,
  affaire,
  title = "Questionnaire Succession",
}) {
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!open) setCopied(null);
  }, [open]);

  const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";

  const copyToClipboard = async (field, value) => {
    try {
      await navigator.clipboard.writeText(String(value ?? ""));
      setCopied(field);
      setTimeout(() => setCopied(null), 1200);
    } catch (err) {
      console.error("copy error", err);
    }
  };

  const roleLabel = (v) => {
    if (!v) return null;
    const map = { heritier: "Héritier", conjointsurvivant: "Conjoint survivant", mandataire: "Mandataire" };
    const val = Array.isArray(v) ? v[0] : v;
    return map[val] || val;
  };

  const testamentLabel = (v) => {
    if (!v) return null;
    const map = { ouiolographe: "Oui (olographe)", oui: "Oui", non: "Non", nsp: "Ne sait pas" };
    const val = Array.isArray(v) ? v[0] : v;
    return map[val] || val;
  };

  const situationLabel = (v) => {
    if (!v) return null;
    const map = { oui: "Marié(e)", veuf: "Veuf / Veuve", divorce: "Divorcé(e)", celibataire: "Célibataire" };
    const val = Array.isArray(v) ? v[0] : v;
    return map[val] || val;
  };

  const bienImmobilierLabel = (v) => {
    if (!v) return null;
    const map = { ouiun: "Oui (un bien)", ouiplusieurs: "Oui (plusieurs biens)", non: "Non" };
    const val = Array.isArray(v) ? v[0] : v;
    return map[val] || val;
  };

  const items = useMemo(() => {
    const a = affaire || {};
    return [
      { key: "role_succession", label: "Rôle dans la succession", value: roleLabel(a.role_succession) },
      { key: "testament", label: "Testament", value: testamentLabel(a.testament) },
      { key: "situation_defunt", label: "Situation matrimoniale du défunt", value: situationLabel(a.situation_defunt) },
      { key: "bien_immobilier", label: "Bien immobilier dans la succession", value: bienImmobilierLabel(a.bien_immobilier) },
      { key: "nb_heritier", label: "Nombre d'héritiers", value: a.nb_heritier },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affaire]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/60" />

      <div className="fixed inset-0 p-4 flex items-center justify-center">
        <DialogPanel className="pointer-events-auto w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {title}
            </DialogTitle>
            <button
              onClick={onClose}
              className="rounded-md p-2 text-gray-400 hover:text-gray-600"
              aria-label="Fermer"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-6">
            <div className="overflow-hidden rounded-xl ring-1 ring-black/5 bg-white divide-y divide-gray-200">
              {items.map((it) => (
                <dl key={it.key} className="divide-y divide-gray-200">
                  <div className="grid grid-cols-1 sm:grid-cols-12 items-start gap-3 p-4 sm:p-5">
                    <dt className="sm:col-span-4 text-sm font-medium text-gray-700">
                      {it.label}
                    </dt>
                    <dd className="sm:col-span-7 text-sm text-black font-bold whitespace-pre-wrap break-words">
                      {hasValue(it.value) ? (
                        String(it.value)
                      ) : (
                        <span className="font-normal text-gray-400">Non renseigné</span>
                      )}
                    </dd>
                    <div className="sm:col-span-1 flex sm:justify-end">
                      <button
                        type="button"
                        onClick={() => copyToClipboard(it.key, it.value)}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        title="Copier"
                      >
                        {copied === it.key ? (
                          <>
                            <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-600" />
                            Copié
                          </>
                        ) : (
                          <>
                            <ClipboardIcon className="h-4 w-4 text-gray-500" />
                            Copier
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </dl>
              ))}
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
